import React, {
  RefObject,
  useEffect,
  useRef,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import defer from 'lodash/defer';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Controller from 'core/components/FormController';
import {
  settingsEventChannel,
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  SaveMasterTemplateRequestDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  useNotify,
} from 'core/hooks';
import {
  useSaveMasterTemplate,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  isEmptyString,
} from '@populateui/utils';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import {
  filterCodes,
  formatBillingCodesForVisit,
  normalizeAssessmentCodeOption,
  normalizeCustomServiceCodeOption,
  normalizeServiceCodeOption,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  useGetFavoriteCodes,
} from 'views/EMR/Settings/hooks';
import TextArea from 'core/components/TextArea';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import MasterTemplateButton from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MasterTemplateButton';
import {
  ToolbarAction,
} from 'views/EMR/components/TextAreaToolbar';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import ReadonlyNote from 'views/EMR/Settings/VisitNotes/ReadonlyNote';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';

export type Props = SaveMasterTemplateRequestDTO;

type FormProps = SidePanelComponentProps & {
  readonly?: boolean;
};

export const serviceCodesFormKeys = {
  CPTCode: 'billingCodes',
  ICDCode: 'assessmentCodes',
};

export const visitNoteFormTemplateAction: ToolbarAction = {
  name: 'template',
  params: {
    controls: ['use'],
  },
};

export default function VisitNoteForm({ entity, readonly = false, onClose }: FormProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const templateNameInputRef = useRef<HTMLInputElement>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
  } = useFormContext<Props>();
  const assessmentCodes = useWatch({ control, name: 'assessmentCodes' }) ?? [];
  const notify = useNotify();
  const { mutateAsync: saveMasterTemplate } = useSaveMasterTemplate();
  const isNew = isEmptyString(entity?.templateName);

  const scrollToBottom = () => {
    const visitNoteContainer = contentRef.current;
    setTimeout(() => {
      if (!isNil(visitNoteContainer)) {
        visitNoteContainer.scrollTop = visitNoteContainer.scrollHeight;
      }
    }, 0);
  };

  const {
    data: favoriteCodes,
    refetch: refetchFavoriteCode,
  } = useGetFavoriteCodes();

  const onSubmit = async (data: Props) => {
    try {
      const template = await saveMasterTemplate({
        ...data,
        billingCodes: formatBillingCodesForVisit(
          data?.billingCodes ?? [],
          data?.assessmentCodes ?? [],
        ),
        isSavingANewTemplate: isNew,
      });
      notify({
        message: 'Master Template Saved Successfully',
        variant: 'success',
      });
      settingsEventChannel.emit('onVisitNoteSettingsChange', 'master-templates', {
        templateName: template?.templateName ?? '',
        userId: template?.userId,
      });
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  useEffect(() => {
    const unsubscribeFromServiceCodesUpdateChannel = visitNoteEventChannel.on('onServiceCodesUpdate', (type) => {
      if (type === 'CustomCode') {
        scrollToBottom();
      }
    });

    defer(() => {
      templateNameInputRef?.current?.focus?.();
    });

    return () => {
      unsubscribeFromServiceCodesUpdateChannel();
    };
  }, []);

  return (
    <div className="w-[40rem] h-full flex flex-col">
      <div className="flex w-full gap-2 justify-between !items-start border-b border-b-gray-300 p-4 populate-form">
        <div className="flex flex-col gap-2 w-full">
          <Controller
            name="templateName"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { invalid } }) => (
              isNew
                ? (
                  <Input
                    {...field}
                    error={invalid}
                    label="Template Name"
                    className="flex-1"
                    value={field.value ?? ''}
                    id="templateName"
                    inputRef={templateNameInputRef}
                    placeholder="Enter template name"
                    allowSpeechToText
                    required
                  />
                )
                : <h3 className="font-semibold">{(field)?.value ?? '--'}</h3>
            )}
          />
          {!readonly && (
            <MasterTemplateButton
              disabled={isSubmitting}
              buttonText="Use Existing Template"
              billingKeysMapping={{
                assessmentCodes: 'assessmentCodes',
                serviceCodes: 'billingCodes',
                customServiceCodes: 'customServiceCodes',
              }}
            />
          )}
        </div>
        <div className="flex h-10 relative -top-4 -right-4">
          <IconButton onClick={onClose}>
            <CloseIcon className="!w-5 !h-5 !text-inherit" />
          </IconButton>
        </div>
      </div>
      <div
        className="flex-1 w-full flex overflow-auto px-4 populate-form"
        ref={contentRef as RefObject<HTMLDivElement>}
      >
        <div className="flex-1 flex flex-col gap-4 h-full w-full mt-4">
          <Controller
            name="additionalHPI"
            control={control}
            render={({ field }) => (
              <Wrapper
                id="additional-hpi"
                label="Additional HPI"
              >
                <TextArea
                  id={VisitNoteSOAPSectionNames.AdditionalHPITemplate}
                  {...field}
                  value={field.value ?? ''}
                  disabled={readonly}
                  actions={['dictation', visitNoteFormTemplateAction, 'field']}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="physicalExam"
            control={control}
            render={({ field }) => (
              <Wrapper
                id="physicalExam"
                label="Objective: Physical Exam"
              >
                <TextArea
                  id={VisitNoteSOAPSectionNames.PhysicalExamTemplate}
                  {...field}
                  value={field.value ?? ''}
                  disabled={readonly}
                  actions={['dictation', visitNoteFormTemplateAction, 'field']}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="radiology"
            control={control}
            render={({ field }) => (
              <Wrapper
                id="radiology"
                label="Review of Imaging"
              >
                <TextArea
                  id={VisitNoteSOAPSectionNames.RadiologyTemplate}
                  {...field}
                  value={field.value ?? ''}
                  disabled={readonly}
                  actions={['dictation', visitNoteFormTemplateAction, 'field']}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="assessmentCodes"
            control={control}
            render={({ field }) => (
              <ServicesLookup
                {...field}
                value={field?.value as VisitNoteServiceCode[] ?? []}
                type="ICDCode"
                label="Assessment (ICD-10)"
                dropdownId="icd-code-selection"
                id="assessmentCodes"
                placeholder="Search diagnosis"
                saveVisitNote={handleSubmit(onSubmit)}
                favorites={favoriteCodes?.favoritedAssessmentCodes ?? []}
                externalFilter={filterCodes}
                refetchFavoriteCode={refetchFavoriteCode}
                classes={{
                  root: 'w-full',
                  table: 'w-[800px]',
                  label: 'text-xs text-gray-500',
                }}
                className="!gap-2"
                actions={['pin']}
                readonly={readonly}
                normalizeOption={normalizeAssessmentCodeOption}
              />
            )}
          />
          <Controller
            name="additionalAssessment"
            control={control}
            render={({ field }) => (
              <Wrapper
                id="additionalAssessment"
              >
                <TextArea
                  id={VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate}
                  {...field}
                  value={field.value ?? ''}
                  disabled={readonly}
                  actions={['dictation', 'field']}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="plan"
            control={control}
            render={({ field }) => (
              <Wrapper
                label="Plan"
                id="plan"
              >
                <TextArea
                  id={VisitNoteSOAPSectionNames.PlanTemplate}
                  {...field}
                  value={field.value ?? ''}
                  disabled={readonly}
                  actions={['dictation', visitNoteFormTemplateAction, 'field']}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="billingCodes"
            control={control}
            render={({ field }) => (
              <ServicesLookup
                {...field}
                value={field?.value as VisitNoteServiceCode[] ?? []}
                type="CPTCode"
                formKeys={serviceCodesFormKeys}
                label="Service Codes"
                placeholder="Search Service Code"
                dropdownId="cpt-code-selection"
                id="billingCodes"
                allowDuplicate
                assessmentCodes={assessmentCodes}
                externalFilter={filterCodes}
                refetchFavoriteCode={refetchFavoriteCode}
                favorites={favoriteCodes?.favoritedBillingCodes ?? []}
                actions={['pin']}
                classes={{
                  root: 'w-full',
                  label: 'text-xs text-gray-500',
                }}
                readonly={readonly}
                normalizeOption={normalizeServiceCodeOption}
              />
            )}
          />
          <Controller
            name="customServiceCodes"
            control={control}
            render={({ field }) => (
              <ServicesLookup
                {...field}
                value={field?.value as VisitNoteServiceCode[] ?? []}
                type="CustomCode"
                placeholder="Search Custom Services"
                label="Custom Services"
                dropdownId="custom-code-selection"
                id="customServiceCodes"
                favorites={favoriteCodes?.favoriteCustomServiceCodes ?? []}
                refetchFavoriteCode={refetchFavoriteCode}
                className="pb-4"
                classes={{
                  root: 'w-full',
                  table: 'w-[400px]',
                  label: 'text-xs text-gray-500',
                }}
                actions={['pin']}
                readonly={readonly}
                normalizeOption={normalizeCustomServiceCodeOption}
              />
            )}
          />
        </div>
      </div>
      <div className="flex justify-between items-center w-full border-t border-t-gray-300 p-4">
        <div className="flex gap-4">
          {!readonly && (
            <Button
              color="primary"
              variant="contained"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
        <ReadonlyNote readonly={readonly} />
      </div>
    </div>
  );
}
